import { skipToken, useQuery } from '@tanstack/react-query';

import { withByosImgixServerSign } from '@air/component-imgix-image';

import { normalizeSvgForImgPreview } from '~/utils/normalizeSvgForImgPreview';

/**
 * Larger SVGs fall back to the caller's existing image. Normalizing parses the SVG on the main thread, and the
 * resulting data URL is up to ~3x the source size and can be held indefinitely by the drag-preview image
 * cache, so the cap bounds both parse cost and memory. Logos and icons are well under this size.
 */
export const MAX_SVG_PREVIEW_BYTES = 256_000;

export const NORMALIZED_SVG_PREVIEW_KEY = 'NORMALIZED_SVG_PREVIEW';

export type NormalizedSvgPreviewParams = {
  src: string | undefined;
};

export type NormalizedSvgPreviewKey = [typeof NORMALIZED_SVG_PREVIEW_KEY, NormalizedSvgPreviewParams];

export const getNormalizedSvgPreviewKey = (params: NormalizedSvgPreviewParams): NormalizedSvgPreviewKey => [
  NORMALIZED_SVG_PREVIEW_KEY,
  params,
];

export type UseNormalizedSvgPreviewQueryOptions = {
  enabled?: boolean;
};

type FetchNormalizedSvgPreviewUrlParams = {
  src: string;
  signal: AbortSignal;
};

/** Returns the body as text, or null when it exceeds `MAX_SVG_PREVIEW_BYTES`. */
const readSvgTextWithinCap = async (response: Response) => {
  if (Number(response.headers.get('content-length')) > MAX_SVG_PREVIEW_BYTES) {
    // Nothing will read the body, so stop the download instead of leaving it to finish in the background.
    await response.body?.cancel();
    return null;
  }

  // content-length can be missing (chunked/compressed responses), so the length is checked after reading.
  const text = await response.text();

  return text.length > MAX_SVG_PREVIEW_BYTES ? null : text;
};

/**
 * `signal` aborts the fetch when every observer unmounts. The resulting AbortError is deliberately not caught:
 * React Query treats it as a cancellation and reverts the query rather than caching a null preview.
 */
const fetchNormalizedSvgPreviewUrl = async ({ src, signal }: FetchNormalizedSvgPreviewUrlParams) => {
  const response = await fetch(withByosImgixServerSign(src), { signal });

  // Throw rather than return null: a null result is cached forever, and a 429 or 5xx can be transient. As an error,
  // callers still fall back to the Imgix image and the next mount retries.
  if (!response.ok) {
    throw new Error(`SVG preview fetch failed with status ${response.status}`);
  }

  const svgText = await readSvgTextWithinCap(response);

  if (svgText === null) {
    return null;
  }

  const normalized = normalizeSvgForImgPreview(svgText);

  if (!normalized) {
    return null;
  }

  const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(normalized)}`;

  // Resolve only after decode so the swap paints immediately, with no blank or collapsed frame.
  const image = new Image();
  image.src = dataUrl;

  try {
    await image.decode();
  } catch {
    return null;
  }

  return dataUrl;
};

/**
 * Resolves to a data URL of the normalized original SVG, or null when the SVG can't be normalized. Callers fall
 * back to their existing image while loading, on error, when disabled, or on null.
 */
export const useNormalizedSvgPreview = (
  params: NormalizedSvgPreviewParams,
  options?: UseNormalizedSvgPreviewQueryOptions,
) => {
  const { src } = params;

  return useQuery({
    queryKey: getNormalizedSvgPreviewKey(params),
    queryFn: src ? ({ signal }) => fetchNormalizedSvgPreviewUrl({ src, signal }) : skipToken,
    enabled: !!src && options?.enabled !== false,
    // The original file for a given URL doesn't change, so never refetch it.
    staleTime: Infinity,
    retry: false,
  });
};
